/**
 * Low-level mutation primitives. These are the building blocks bespoke flows
 * compose with — they replace ad-hoc useRef<Map> race handling, manual
 * snapshot/rollback bookkeeping, and inline setQueryData shuffles.
 *
 * Each primitive is independently testable with a mock QueryClient.
 */

import { type QueryClient, type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import type { ApiError } from '@/api/apiUtils';

type Prefix = readonly unknown[];

/**
 * Snapshot-patch-rollback primitive for optimistic updates on a known cache key.
 *
 * The caller takes a snapshot before patching, then either commits (no-op) or
 * rolls back to the snapshot on error. This is the missing piece useUpdateScene
 * was reimplementing manually.
 */
export function useOptimisticPatch<T>(keyFn: (id: number) => Prefix) {
    const queryClient = useQueryClient();

    return {
        snapshot(id: number): T | undefined {
            return queryClient.getQueryData<T>(keyFn(id));
        },
        async cancelInFlight(id: number) {
            await queryClient.cancelQueries({ queryKey: keyFn(id) });
        },
        patch(id: number, updater: (old: T | undefined) => T | undefined) {
            queryClient.setQueryData<T>(keyFn(id), updater);
        },
        rollback(id: number, prev: T | undefined) {
            if (prev !== undefined) {
                queryClient.setQueryData<T>(keyFn(id), prev);
            }
        },
    };
}

/**
 * Snapshot-patch-rollback over EVERY cached query under a key prefix.
 *
 * The list variant of useOptimisticPatch: consumers may cache the same list
 * under several concrete keys (different filter/version params), and an
 * optimistic insert/remove must hit all of them to be visible everywhere.
 */
export function useOptimisticPrefixPatch<T>(prefix: Prefix) {
    const queryClient = useQueryClient();

    return {
        async cancelInFlight() {
            await queryClient.cancelQueries({ queryKey: prefix });
        },
        snapshot(): Array<[readonly unknown[], T | undefined]> {
            return queryClient.getQueriesData<T>({ queryKey: prefix });
        },
        patch(updater: (old: T | undefined) => T | undefined) {
            queryClient.setQueriesData<T>({ queryKey: prefix }, updater);
        },
        rollback(snapshots: Array<[readonly unknown[], T | undefined]>) {
            for (const [key, data] of snapshots) {
                queryClient.setQueryData(key, data);
            }
        },
    };
}

/**
 * Direct cache write — mutate a query's cached value without triggering a refetch.
 * Useful for syncing computed state into a list cache after a successful mutation.
 */
export function useDirectCacheWrite<T>(key: Prefix) {
    const queryClient = useQueryClient();
    return (updater: (old: T | undefined) => T | undefined) => {
        queryClient.setQueryData<T>(key, updater);
    };
}

interface SafeCreateOrUpdateOpts<TArgs, TResult> {
    /** Stable string identity for race-deduplication (e.g. `scene:${id}:dialogue`). */
    identityKey: (args: TArgs) => string;
    /** Whether the args carry an existing id (skip create entirely). */
    hasId: (args: TArgs) => number | undefined;
    /** Create the resource. Resolves to the new id and the response payload. */
    create: (args: TArgs) => Promise<{ id: number; data: TResult }>;
    /** Update an existing resource by id. */
    update: (args: TArgs, id: number) => Promise<TResult>;
}

/** What every coordinator on one query client knows about its identities. */
interface CreateOrUpdateState {
    /** A created id, until its owner can name it (`forget`). */
    ids: Map<string, number>;
    /** A create on the wire. */
    pending: Map<string, Promise<number>>;
}

const createOrUpdateStates = new WeakMap<QueryClient, CreateOrUpdateState>();

function createOrUpdateStateOf(queryClient: QueryClient): CreateOrUpdateState {
    let state = createOrUpdateStates.get(queryClient);
    if (!state) {
        state = { ids: new Map(), pending: new Map() };
        createOrUpdateStates.set(queryClient, state);
    }
    return state;
}

export interface SafeCreateOrUpdate<TArgs, TResult> {
    (args: TArgs): Promise<TResult>;
    /** Drop the created id remembered for `args`' identity — call it once the
     *  save that created it has settled and the id is somewhere every caller
     *  reads it from (the list, joined or re-read). Until then an id-less call
     *  updates the created resource; after, it creates again, which is what «no
     *  id» means once the list could have named one (an atom set aside as a
     *  draft is not the field's atom any more). */
    forget: (args: TArgs) => void;
}

/**
 * Race-safe "create if missing, otherwise update" coordinator.
 *
 * A create on the wire is deduped per identity: a call that arrives meanwhile
 * awaits it and updates what it made. The state belongs to the QUERY CLIENT,
 * not the hook instance — two surfaces that write the same resource (the
 * paper's scene save and the Navigator's logline cell) share one identity, so
 * neither can create what the other is already creating. Identity strings are
 * therefore one namespace across the app: prefix them by entity and field.
 */
export function useSafeCreateOrUpdate<TArgs, TResult>(opts: SafeCreateOrUpdateOpts<TArgs, TResult>): SafeCreateOrUpdate<TArgs, TResult> {
    const { ids, pending } = createOrUpdateStateOf(useQueryClient());
    const optsRef = useRef(opts);
    optsRef.current = opts;

    const upsert = async (args: TArgs): Promise<TResult> => {
        const { identityKey, hasId, create, update } = optsRef.current;
        const key = identityKey(args);

        const knownId = hasId(args) ?? ids.get(key);
        if (knownId !== undefined) {
            return update(args, knownId);
        }

        const inFlight = pending.get(key);
        if (inFlight) {
            let id: number;
            try {
                id = await inFlight;
            } catch {
                // The create it waited on made nothing: these args are the
                // newest words, so they create — rather than fail with it.
                if (pending.get(key) === inFlight) pending.delete(key);
                return upsert(args);
            }
            return update(args, id);
        }

        const createPromise = (async () => {
            const { id, data } = await create(args);
            ids.set(key, id);
            return { id, data };
        })();

        const idPromise = createPromise.then(r => r.id);
        // A failed create rejects its waiters through `await inFlight`; this
        // copy only keeps the rejection from surfacing as unhandled.
        idPromise.catch(() => {});
        pending.set(key, idPromise);

        try {
            const { data } = await createPromise;
            return data;
        } finally {
            if (pending.get(key) === idPromise) pending.delete(key);
        }
    };

    return Object.assign(upsert, {
        forget: (args: TArgs) => {
            ids.delete(optsRef.current.identityKey(args));
        },
    });
}

interface CustomMutationOpts<TParams, TData, TCtx> {
    mutationFn: (params: TParams) => Promise<TData>;
    onMutate?: (params: TParams) => Promise<TCtx> | TCtx;
    onError?: (error: ApiError, params: TParams, ctx: TCtx | undefined) => void;
    onSuccess?: (data: TData, params: TParams, ctx: TCtx | undefined) => void;
    onSettled?: (data: TData | undefined, error: ApiError | null, params: TParams, ctx: TCtx | undefined) => void;
    /** Empty array = explicit skip. Non-empty = invalidate each prefix on settle. */
    invalidate?: readonly Prefix[];
    /** Forwarded to TanStack `useMutation` meta field. */
    meta?: Record<string, unknown>;
}

/**
 * Escape-hatch mutation hook for flows that don't fit the registry. Provides
 * the standard onMutate/onError/onSuccess shape with explicit invalidation —
 * no implicit cache touching — so the behavior is auditable.
 */
export function useCustomMutation<TParams, TData = unknown, TCtx = unknown>(
    opts: CustomMutationOpts<TParams, TData, TCtx>,
): UseMutationResult<TData, ApiError, TParams, TCtx> {
    const queryClient = useQueryClient();
    const { mutationFn, onMutate, onError, onSuccess, onSettled, invalidate, meta } = opts;

    return useMutation<TData, ApiError, TParams, TCtx>({
        mutationFn,
        meta,
        onMutate,
        onError: (error, params, ctx) => onError?.(error, params, ctx),
        onSuccess: (data, params, ctx) => onSuccess?.(data, params, ctx),
        onSettled: (data, error, params, ctx) => {
            if (invalidate && invalidate.length > 0) {
                for (const key of invalidate) {
                    queryClient.invalidateQueries({ queryKey: key as unknown[] });
                }
            }
            onSettled?.(data, error, params, ctx);
        },
    });
}
